import * as React from "react";
import * as moment from "moment";
import * as _ from "lodash";
import { IContact, ILogisticsProvider, ITransportType } from "@nkr/models/index";
import { LogisticsAudience, TransportMethod } from "@nkr/enums/index";
import { TableHeadWrapper, LogisticsTableBodyWrapper, TableBodyWrapperOptions } from "../../../styles";
import { FormControlStaticWrapper, FormControlWrapper } from "../../styles";
import { Glyphicon, FormControl, ToggleButtonGroup, ToggleButton, Button } from "react-bootstrap";
import SelectTimezone from "./select-timezone";
import { ILogisticsOptionState } from "./logistics-option-state";
import styled from "styled-components";
import { charterLegs } from "./leg-types";
import MaskedInput from "react-maskedinput";
import { TableWrapper, TimezoneValueWrapper } from "./styles";
import DateTimePickerMasked from "@nkr/components/date-time-picker-masked";

const singleLegs: string[] = ["Commercial", "Charter", "Ground", "Internal", "Comm OBC - Single Leg"];
export const optionColors: string[] = ['pink', 'peachpuff', 'lightgoldenrodyellow', 'palegreen', 'paleturquoise', 'lightsteelblue', 'thistle'];

const RemoveOptionRow = styled.tr`
    & > td > button {
        margin-top: 5px;;
    }
`;

interface IProps {
    data: ILogisticsOptionState,
    swapNumber: string,
    donorCenter: string,
    donorAlias: string,
    rdnCenterId: number,
    rdnCenterAlias: string
    recipientCenter: string,
    recipientAlias: string,
    earliestPickup: string,
    latestDropoff: string,
    logisticsProviders: ILogisticsProvider[],
    transportTypes: ITransportType[],
    donorSurgeons: IContact[],
    recipientSurgeons: IContact[],
    validDeadline: boolean,
    audience: LogisticsAudience,
    readonly: boolean,
    dirty: boolean,
    allowedOptions: number,
    onLogisticsProviderChange: (value: string) => void,
    onDeadlineChange: (value: string) => void,
    onDateTimeFieldChange: (option: number, field: string, value: moment.Moment | string) => void,
    onFieldChange: (option: number, field: string, value: string) => void,
    onDropdownChange: (option: number, id: string, value: string) => void,
    onRemoveOption: (option: number) => void,
    onClearOption: (option: number) => void
}

interface IState {
    selectedOption: number[],
    optionList: { index: number, display: boolean, color: string }[],
    pageSize: number,
    currentPage: number
}

class LogisticsOptionContent extends React.Component<IProps, IState> {
    readonly state: IState = {
        selectedOption: _.range(0, this.props.allowedOptions).map(x => 0),
        optionList: _.range(0, this.props.allowedOptions).map(x => ({ index: x, display: x <= 2, color: optionColors[x] })),
        pageSize: 3,
        currentPage: 0
    }

    handleOptionChange = (option: number, primaryBackup: number) => {
        let selectedOption = [...this.state.selectedOption];
        selectedOption[option] = primaryBackup;

        this.setState({
            selectedOption
        });
    }

    handleLogisticsProviderChange = (e: React.FormEvent<FormControl & HTMLSelectElement>) => {
        this.props.onLogisticsProviderChange(e.currentTarget.value);
    }

    handleDeadlineChange = (e: React.FormEvent<FormControl & HTMLInputElement>) => {
        this.props.onDeadlineChange(e.currentTarget.value);
    }

    handleDateTimeFieldChange = (option: number, field: string, e: string) => {
        this.props.onDateTimeFieldChange(option, field, e);
    }

    handleFieldChange = (option: number, e: React.FormEvent<FormControl & HTMLInputElement>) => {
        let field = e.currentTarget.id;
        this.props.onFieldChange(option, field, e.currentTarget.value);
    }

    handleDropdownChange = (option: number, id: string, e: React.FormEvent<FormControl & HTMLSelectElement>) => {
        this.props.onDropdownChange(option, id, e.currentTarget.value);
    }

    handleTimezoneChange = (option: number, field: string, value: string) => {
        this.props.onFieldChange(option, field, value);
    }

    handleRemoveOption = (option: number) => {
        this.props.onRemoveOption(option);
    }

    handleClearOption = (option: number) => {
        this.props.onClearOption(option);
    }

    handleShowPrevious = () => {
        let currentPage = this.state.currentPage - 1;
        let start = currentPage * this.state.pageSize;
        let end = start + 3;

        let page = _.slice(this.state.optionList, start, end);

        let optionList = _.cloneDeep(this.state.optionList).map(x => {
            let f = page.find(p => p.index == x.index);
            x.display = f != undefined
            return x;
        });

        this.setState({
            optionList,
            currentPage
        });
    }

    handleShowNext = () => {
        let currentPage = this.state.currentPage + 1;
        let start = currentPage * this.state.pageSize - (this.props.allowedOptions % 3 == 0 ? 0 : 1);
        let end = start + 3;

        let page = _.slice(this.state.optionList, start, end);

        let optionList = _.cloneDeep(this.state.optionList).map(x => {
            let f = page.find(p => p.index == x.index);
            x.display = f != null
            return x;
        });

        this.setState({
            optionList,
            currentPage
        });
    }

    render = () => {
        const { swapNumber, donorCenter, donorAlias, rdnCenterId, rdnCenterAlias, recipientCenter, recipientAlias, earliestPickup, latestDropoff } = this.props;

        let leg1Disabled: boolean[] = this.state.optionList.map(x => true);
        let leg2Disabled: boolean[] = this.state.optionList.map(x => true);
        let charters: boolean[] = this.state.optionList.map(x => false);

        const optionList = this.state.optionList.filter(x => x.display);

        const backgroundColor = '#99BDDB';

        const transportTypes = this.props.transportTypes.filter(x => x.id != TransportMethod.Unknown && x.id != TransportMethod.CommOneLegObc);

        const showPreviousButton = _.maxBy(optionList, item => item.index).index > 2;
        const showNextButton = _.maxBy(optionList, item => item.index).index <= 2;

        return (
            <>
                <div style={{ border: "1px solid #bce8f1", backgroundColor: "#d9edf7", color: "#31708f", marginTop: 5, marginBottom: 10, padding: 7, display: "flex", justifyContent: "space-around", alignItems: "center", borderRadius: 4 }}>
                    <div className="text-center">
                        <span style={{ fontWeight: 700 }}>Swap #</span><br />
                        <span>{swapNumber}</span>
                    </div>

                    <div style={{ display: 'flex', alignItems: "center" }}>
                        <div className="text-center">
                            <span style={{ fontWeight: 700 }}>{donorCenter}</span>
                            {rdnCenterId > 0 ?
                                <span style={{ paddingLeft: 5 }}>(<span style={{ color: "red" }}>{rdnCenterAlias}</span>)</span>
                                : null}
                            <br />
                            <span>{donorAlias}</span>
                        </div>

                        <div className="text-center" style={{ width: 50 }}>
                            <Glyphicon glyph="forward" />
                        </div>

                        <div className="text-center">
                            <span style={{ fontWeight: 700 }}>{recipientCenter}</span><br />
                            <span>{recipientAlias}</span>
                        </div>
                    </div>

                    <div className="text-center">
                        <span style={{ fontWeight: 700 }}>Earliest Pickup</span><br />
                        <span>{earliestPickup}</span>
                    </div>
                    <div className="text-center">
                        <span style={{ fontWeight: 700 }}>Latest Dropoff</span><br />
                        <span>{latestDropoff}</span>
                    </div>
                </div>

                {this.props.audience == LogisticsAudience.NKR && (
                    <div style={{ color: "#8a6d3b", backgroundColor: "#fcf8e3", border: "1px solid #faebcc", padding: 7, marginBottom: 10 }}>
                        <TableWrapper className="condensed" style={{ marginBottom: 0 }}>
                            <LogisticsTableBodyWrapper>
                                <tr>
                                    <td style={{ textAlign: "right" }}>Logistics Provider:</td>
                                    <td>
                                        <FormControlWrapper
                                            componentClass="select"
                                            value={this.props.data.logisticsProvider}
                                            onChange={this.handleLogisticsProviderChange}
                                        >
                                            <option value="">-- select --</option>
                                            {this.props.logisticsProviders.filter(item => item.isActive).map((item, index) =>
                                                <option key={index} value={item.id}>{item.description}</option>
                                            )}
                                        </FormControlWrapper>
                                    </td>
                                    <td style={{ textAlign: "right" }}>Deadline:</td>
                                    <td>
                                        <MaskedInput
                                            className='form-control'
                                            mask='11/11/11'
                                            placeholder='mm/dd/yy'
                                            style={{ borderColor: this.props.validDeadline ? '#ccc' : '#a94442' }}
                                            value={this.props.data.deadline}
                                            onChange={this.handleDeadlineChange}
                                        />
                                    </td>
                                </tr>
                            </LogisticsTableBodyWrapper>
                        </TableWrapper>
                    </div>
                )}

                <div>
                    <TableWrapper className="table-condensed table-responsive" style={{ borderCollapse: 'separate', borderSpacing: '1px 0px' }}>
                        <TableHeadWrapper>
                            <tr>
                                <th></th>
                                {optionList.map(option =>
                                    <th key={'th-' + option.index} className="text-center" style={{ width: "23.5%", borderTopLeftRadius: 4, borderTopRightRadius: 4, backgroundColor: option.color }}>
                                        <div>Option {option.index + 1}</div>

                                        <ToggleButtonGroup type="radio" name="option1" value={this.state.selectedOption[option.index]}>
                                            <ToggleButton type="radio" value={1} className={this.state.selectedOption[option.index] == 0 ? "btn-primary btn-xs" : "btn-xs"} onClick={() => this.handleOptionChange(option.index, 0)}>Primary</ToggleButton>
                                            <ToggleButton type="radio" value={2} className={this.state.selectedOption[option.index] == 1 ? "btn-primary btn-xs" : "btn-xs"} onClick={() => this.handleOptionChange(option.index, 1)}>Backup</ToggleButton>
                                        </ToggleButtonGroup>
                                    </th>
                                )}
                            </tr>
                        </TableHeadWrapper>
                        <TableBodyWrapperOptions>
                            <tr>
                                <td style={{ fontWeight: 700 }}>Donor Surgery/Pickup Date/Time</td>

                                {optionList.map(option => {
                                    const property = this.state.selectedOption[option.index] == 0 ? 'kidneyPickupDateTime' : 'kidneyPickupDateTimeBackup';
                                    const value = this.state.selectedOption[option.index] == 0 ? this.props.data.options[option.index].kidneyPickupDateTime : this.props.data.options[option.index].kidneyPickupDateTimeBackup;
                                    const valid = this.state.selectedOption[option.index] == 0 ? this.props.data.options[option.index].kidneyPickupDateTimeValid : this.props.data.options[option.index].kidneyPickupDateTimeBackupValid;
                                    const tz = this.props.data.options[option.index].kidneyPickupDateTimeTz;
                                    const style = this.state.selectedOption[option.index] == 1 ? { backgroundColor } : null;

                                    return (
                                        <td key={`kpdt${option.index}`} style={style}>
                                            <div style={{ display: "flex" }}>
                                                <DateTimePickerMasked
                                                    property={property}
                                                    currentValue={value}
                                                    borderColor={valid ? "#ccc" : "#a94442"}
                                                    dateFormat='MM/DD/YY'
                                                    disabled={this.props.readonly}
                                                    onValueChanged={(property, value) => this.handleDateTimeFieldChange(option.index, property, value)}
                                                />
                                                <TimezoneValueWrapper className="form-control">{tz}</TimezoneValueWrapper>
                                            </div>
                                        </td>
                                    );
                                })}
                            </tr>

                            <tr>
                                <td style={{ fontWeight: 700 }}>Transport Method</td>

                                {optionList.map(option => {
                                    const property = this.state.selectedOption[option.index] == 0 ? 'transportMethod' : 'transportMethodBackup';
                                    const value = this.state.selectedOption[option.index] == 0 ? this.props.data.options[option.index].transportMethod : this.props.data.options[option.index].transportMethodBackup;
                                    const valid = this.state.selectedOption[option.index] == 0 ? this.props.data.options[option.index].transportMethodValid : this.props.data.options[option.index].transportMethodBackupValid;
                                    const transportType = transportTypes.find(tt => tt.id == (value != "" ? parseInt(value) : 0));
                                    const transportTypeDescription = transportType ? transportType.description : '';
                                    const isCharter = charterLegs.indexOf(transportTypeDescription) >= 0;
                                    const disableLeg2 = transportType != null ? singleLegs.indexOf(transportTypeDescription) >= 0 : false;
                                    const style = this.state.selectedOption[option.index] == 1 ? { backgroundColor } : null;

                                    leg1Disabled[option.index] = (transportType ? transportType.id : -1) == TransportMethod.Ground;
                                    leg2Disabled[option.index] = disableLeg2;
                                    charters[option.index] = isCharter;

                                    return (
                                        <td key={`tm${option.index}`} style={style}>
                                            <FormControlWrapper
                                                componentClass="select"
                                                value={value}
                                                id={property}
                                                disabled={this.props.readonly}
                                                style={{ borderColor: valid ? "#ccc" : "#a94442" }}
                                                onChange={this.handleDropdownChange.bind(this, option.index, property)}
                                            >
                                                <option value=''>- select -</option>
                                                {transportTypes.map((tt, index) =>
                                                    <option key={index} value={tt.id}>{tt.description}</option>
                                                )}
                                            </FormControlWrapper>
                                        </td>
                                    );
                                })}
                            </tr>

                            <tr>
                                <td style={{ fontWeight: 700 }}>Flight Route or Ground</td>

                                {optionList.map(option => {
                                    const property = this.state.selectedOption[option.index] == 0 ? 'flightRouteOrGround' : 'flightRouteOrGroundBackup';
                                    const value = this.state.selectedOption[option.index] == 0 ? this.props.data.options[option.index].flightRouteOrGround : this.props.data.options[option.index].flightRouteOrGroundBackup;
                                    const valid = this.state.selectedOption[option.index] == 0 ? this.props.data.options[option.index].flightRouteOrGroundValid : this.props.data.options[option.index].flightRouteOrGroundBackupValid;
                                    const style = this.state.selectedOption[option.index] == 1 ? { backgroundColor } : null;

                                    return (
                                        <td key={`ftg${option.index}`} style={style}>
                                            <FormControlWrapper
                                                type="text"
                                                value={value}
                                                id={property}
                                                disabled={this.props.readonly}
                                                style={{ borderColor: valid ? "#ccc" : "#a94442" }}
                                                onChange={this.handleFieldChange.bind(this, option.index)}
                                            />
                                        </td>
                                    );
                                })}
                            </tr>

                            <tr>
                                <td style={{ fontWeight: 700 }}>{this.props.audience != LogisticsAudience.NKR ? 'Transportation Cost' : 'Transportation Cost/Upcharge'}</td>

                                {optionList.map(option => {
                                    const property = this.state.selectedOption[option.index] == 0 ? 'transportCost' : 'transportCostBackup';
                                    const value = this.state.selectedOption[option.index] == 0 ? this.props.data.options[option.index].transportCost : this.props.data.options[option.index].transportCostBackup;
                                    const upChargeValue = this.state.selectedOption[option.index] == 0 ? this.props.data.options[option.index].upCharge : this.props.data.options[option.index].upChargeBackup;
                                    const valid = this.state.selectedOption[option.index] == 0 ? this.props.data.options[option.index].transportCostValid : this.props.data.options[option.index].transportCostBackupValid;
                                    const style = this.state.selectedOption[option.index] == 1 ? { backgroundColor } : null;
                                    const isCharter = charters[option.index];

                                    if (!isCharter) {
                                        const isCourier = this.props.audience === LogisticsAudience.Courier;
                                        return (
                                            <td key={`tc${option.index}`} style={style}>
                                                <div>
                                                    {isCourier ? (
                                                        <button style={{ width: "100%" }} className="btn btn-sm btn-primary" disabled>Terms & Conditions</button>
                                                    ) : (
                                                        <a href="https://www.kidneyregistry.com/membership-terms-legacy">
                                                            <button style={{ width: "100%" }} className="btn btn-sm btn-primary">Terms & Conditions</button>
                                                        </a>
                                                    )}
                                                </div>
                                            </td>
                                        );
                                    } else {
                                        return (
                                            <td key={`tc${option.index}`} style={style}>
                                                <div style={{ display: 'flex' }}>
                                                    <div style={{ marginRight: 2, flexBasis: this.props.audience == LogisticsAudience.NKR ? 'auto' : '50%', flexGrow: 1 }}>
                                                        <FormControlWrapper
                                                            type="text"
                                                            value={value}
                                                            id={property}
                                                            disabled={this.props.readonly}
                                                            style={{ borderColor: valid ? "#ccc" : "#a94442" }}
                                                            onChange={this.handleFieldChange.bind(this, option.index)}
                                                        />
                                                    </div>
                                                    {this.props.audience == LogisticsAudience.NKR && (
                                                        <div style={{ flexBasis: '50%', flexGrow: 1 }}>
                                                            <FormControlStaticWrapper>{upChargeValue}</FormControlStaticWrapper>
                                                        </div>
                                                    )}
                                                </div>
                                            </td>
                                        );
                                    }
                                })}
                            </tr>

                            <tr>
                                <td style={{ fontWeight: 700 }}>Flight Carrier &amp; Number</td>

                                {optionList.map(option => {
                                    const property = this.state.selectedOption[option.index] == 0 ? 'flightCarrierAndNumber' : 'flightCarrierAndNumberBackup';
                                    const value = this.state.selectedOption[option.index] == 0 ? this.props.data.options[option.index].flightCarrierAndNumber : this.props.data.options[option.index].flightCarrierAndNumberBackup;
                                    const style = this.state.selectedOption[option.index] == 1 ? { backgroundColor } : null;
                                    const disableLeg1 = leg1Disabled[option.index];

                                    return (
                                        <td key={`fcn${option.index}`} style={style}>
                                            <FormControlWrapper
                                                type="text"
                                                value={value}
                                                id={property}
                                                disabled={disableLeg1 || this.props.readonly}
                                                onChange={this.handleFieldChange.bind(this, option.index)}
                                            />
                                        </td>
                                    );
                                })}
                            </tr>

                            <tr>
                                <td style={{ fontWeight: 700 }}>Leg 1 Departure Date/Time</td>

                                {optionList.map(option => {
                                    const property = this.state.selectedOption[option.index] == 0 ? 'flightDepartureTime' : 'flightDepartureTimeBackup';
                                    const propertyTz = this.state.selectedOption[option.index] == 0 ? 'flightDepartureTimeTz' : 'flightDepartureTimeBackupTz';
                                    const value = this.state.selectedOption[option.index] == 0 ? this.props.data.options[option.index].flightDepartureTime : this.props.data.options[option.index].flightDepartureTimeBackup;
                                    const valid = this.state.selectedOption[option.index] == 0 ? this.props.data.options[option.index].flightDepartureTimeValid : this.props.data.options[option.index].flightDepartureTimeBackupValid;
                                    const tz = this.state.selectedOption[option.index] == 0 ? this.props.data.options[option.index].flightDepartureTimeTz : this.props.data.options[option.index].flightDepartureTimeBackupTz;
                                    const style = this.state.selectedOption[option.index] == 1 ? { backgroundColor } : null;
                                    const disableLeg1 = leg1Disabled[option.index];

                                    return (
                                        <td key={`fddt${option.index}`} style={style}>
                                            <div style={{ display: "flex" }}>
                                                <DateTimePickerMasked
                                                    property={property}
                                                    currentValue={value}
                                                    borderColor={valid ? "#ccc" : "#a94442"}
                                                    dateFormat='MM/DD/YY'
                                                    disabled={disableLeg1 || this.props.readonly}
                                                    onValueChanged={(property, value) => this.handleDateTimeFieldChange(option.index, property, value)}
                                                />
                                                <SelectTimezone
                                                    id={propertyTz}
                                                    value={tz}
                                                    disabled={disableLeg1 || this.props.readonly}
                                                    onChange={(field, value) => this.handleTimezoneChange(option.index, field, value)}
                                                />
                                            </div>
                                        </td>
                                    );
                                })}
                            </tr>

                            <tr>
                                <td style={{ fontWeight: 700 }}>Leg 1 Arrival Date/Time</td>

                                {optionList.map(option => {
                                    const property = this.state.selectedOption[option.index] == 0 ? 'flightArrivalTime' : 'flightArrivalTimeBackup';
                                    const propertyTz = this.state.selectedOption[option.index] == 0 ? 'flightArrivalTimeTz' : 'flightArrivalTimeBackupTz';
                                    const value = this.state.selectedOption[option.index] == 0 ? this.props.data.options[option.index].flightArrivalTime : this.props.data.options[option.index].flightArrivalTimeBackup;
                                    const valid = this.state.selectedOption[option.index] == 0 ? this.props.data.options[option.index].flightArrivalTimeValid : this.props.data.options[option.index].flightArrivalTimeBackupValid;
                                    const tz = this.state.selectedOption[option.index] == 0 ? this.props.data.options[option.index].flightArrivalTimeTz : this.props.data.options[option.index].flightArrivalTimeBackupTz;
                                    const style = this.state.selectedOption[option.index] == 1 ? { backgroundColor } : null;
                                    const disableLeg1 = leg1Disabled[option.index];

                                    return (
                                        <td key={`fadt${option.index}`} style={style}>
                                            <div style={{ display: "flex" }}>
                                                <DateTimePickerMasked
                                                    property={property}
                                                    currentValue={value}
                                                    borderColor={valid ? "#ccc" : "#a94442"}
                                                    dateFormat='MM/DD/YY'
                                                    disabled={disableLeg1 || this.props.readonly}
                                                    onValueChanged={(property, value) => this.handleDateTimeFieldChange(option.index, property, value)}
                                                />
                                                <SelectTimezone
                                                    id={propertyTz}
                                                    value={tz}
                                                    disabled={disableLeg1 || this.props.readonly}
                                                    onChange={(field, value) => this.handleTimezoneChange(option.index, field, value)}
                                                />
                                            </div>
                                        </td>
                                    );
                                })}
                            </tr>

                            <tr>
                                <td style={{ fontWeight: 700 }}>Leg 2 Departure Date/Time</td>

                                {optionList.map(option => {
                                    const property = this.state.selectedOption[option.index] == 0 ? 'leg2DepartureTime' : 'leg2DepartureTimeBackup';
                                    const propertyTz = this.state.selectedOption[option.index] == 0 ? 'leg2DepartureTimeTz' : 'leg2DepartureTimeBackupTz';
                                    const value = this.state.selectedOption[option.index] == 0 ? this.props.data.options[option.index].leg2DepartureTime : this.props.data.options[option.index].leg2DepartureTimeBackup;
                                    const valid = this.state.selectedOption[option.index] == 0 ? this.props.data.options[option.index].leg2DepartureTimeValid : this.props.data.options[option.index].leg2DepartureTimeBackupValid;
                                    const tz = this.state.selectedOption[option.index] == 0 ? this.props.data.options[option.index].leg2DepartureTimeTz : this.props.data.options[option.index].leg2DepartureTimeBackupTz;
                                    const disableLeg2 = leg2Disabled[option.index];
                                    const style = this.state.selectedOption[option.index] == 1 ? { backgroundColor } : null;

                                    return (
                                        <td key={`l2ddt${option.index}`} style={style}>
                                            <div style={{ display: "flex" }}>
                                                <DateTimePickerMasked
                                                    property={property}
                                                    currentValue={value}
                                                    borderColor={valid ? "#ccc" : "#a94442"}
                                                    dateFormat='MM/DD/YY'
                                                    disabled={disableLeg2 || this.props.readonly}
                                                    onValueChanged={(property, value) => this.handleDateTimeFieldChange(option.index, property, value)}
                                                />
                                                <SelectTimezone
                                                    id={propertyTz}
                                                    value={tz}
                                                    disabled={disableLeg2 || this.props.readonly}
                                                    onChange={(field, value) => this.handleTimezoneChange(option.index, field, value)}
                                                />
                                            </div>
                                        </td>
                                    );
                                })}
                            </tr>

                            <tr>
                                <td style={{ fontWeight: 700 }}>Leg 2 Arrival Date/Time</td>

                                {optionList.map(option => {
                                    const property = this.state.selectedOption[option.index] == 0 ? 'leg2ArrivalTime' : 'leg2ArrivalTimeBackup';
                                    const propertyTz = this.state.selectedOption[option.index] == 0 ? 'leg2ArrivalTimeTz' : 'leg2ArrivalTimeBackupTz';
                                    const value = this.state.selectedOption[option.index] == 0 ? this.props.data.options[option.index].leg2ArrivalTime : this.props.data.options[option.index].leg2ArrivalTimeBackup;
                                    const valid = this.state.selectedOption[option.index] == 0 ? this.props.data.options[option.index].leg2ArrivalTimeValid : this.props.data.options[option.index].leg2ArrivalTimeBackupValid;
                                    const tz = this.state.selectedOption[option.index] == 0 ? this.props.data.options[option.index].leg2ArrivalTimeTz : this.props.data.options[option.index].leg2ArrivalTimeBackupTz;
                                    const disableLeg2 = leg2Disabled[option.index];
                                    const style = this.state.selectedOption[option.index] == 1 ? { backgroundColor } : null;

                                    return (
                                        <td key={`l2adt${option.index}`} style={style}>
                                            <div style={{ display: "flex" }}>
                                                <DateTimePickerMasked
                                                    property={property}
                                                    currentValue={value}
                                                    borderColor={valid ? "#ccc" : "#a94442"}
                                                    dateFormat='MM/DD/YY'
                                                    disabled={disableLeg2 || this.props.readonly}
                                                    onValueChanged={(property, value) => this.handleDateTimeFieldChange(option.index, property, value)}
                                                />
                                                <SelectTimezone
                                                    id={propertyTz}
                                                    value={tz}
                                                    disabled={disableLeg2 || this.props.readonly}
                                                    onChange={(field, value) => this.handleTimezoneChange(option.index, field, value)}
                                                />
                                            </div>
                                        </td>
                                    );
                                })}
                            </tr>

                            <tr>
                                <td style={{ fontWeight: 700 }}>Kidney Arrival Date/Time</td>

                                {optionList.map(option => {
                                    const property = this.state.selectedOption[option.index] == 0 ? 'kidneyArrivalDateTime' : 'kidneyArrivalDateTimeBackup';
                                    const value = this.state.selectedOption[option.index] == 0 ? this.props.data.options[option.index].kidneyArrivalDateTime : this.props.data.options[option.index].kidneyArrivalDateTimeBackup;
                                    const valid = this.state.selectedOption[option.index] == 0 ? this.props.data.options[option.index].kidneyArrivalDateTimeValid : this.props.data.options[option.index].kidneyArrivalDateTimeBackupValid;
                                    const tz = this.props.data.options[option.index].kidneyArrivalDateTimeTz;
                                    const style = this.state.selectedOption[option.index] == 1 ? { backgroundColor } : null;

                                    return (
                                        <td key={`kadt${option.index}`} style={style}>
                                            <div style={{ display: "flex" }}>
                                                <DateTimePickerMasked
                                                    property={property}
                                                    currentValue={value}
                                                    borderColor={valid ? "#ccc" : "#a94442"}
                                                    dateFormat='MM/DD/YY'
                                                    disabled={this.props.readonly}
                                                    onValueChanged={(property, value) => this.handleDateTimeFieldChange(option.index, property, value)}
                                                />
                                                <TimezoneValueWrapper className="form-control">{tz}</TimezoneValueWrapper>
                                            </div>
                                        </td>
                                    );
                                })}
                            </tr>
                            <RemoveOptionRow>
                                <td style={{ textAlign: 'center' }}>
                                    <Button bsSize='xs' style={{ width: 75, fontWeight: showPreviousButton ? 700 : 400 }} disabled={!showPreviousButton} onClick={this.handleShowPrevious}><Glyphicon glyph='chevron-left' /> Previous</Button>{' '}
                                    <Button bsSize='xs' style={{ width: 75, fontWeight: showNextButton ? 700 : 400 }} disabled={!showNextButton} onClick={this.handleShowNext}>Next <Glyphicon glyph='chevron-right' /></Button>
                                </td>

                                {optionList.map(option =>
                                    <td key={`remove${option.index}`} className="text-center">
                                        <Button bsStyle="primary" bsSize="small" disabled={this.props.readonly} onClick={() => this.handleRemoveOption(option.index)}>Remove Option</Button>
                                        <Button bsSize="small" style={{ marginLeft: 2 }} disabled={this.props.readonly} onClick={() => this.handleClearOption(option.index)}>Clear</Button>
                                    </td>
                                )}
                            </RemoveOptionRow>
                        </TableBodyWrapperOptions>
                    </TableWrapper>
                </div>
            </>
        );
    }
}

export default LogisticsOptionContent;